import { AnchorHTMLAttributes, FC } from 'react';
import { Static_White } from '@croquiscom/zds-core';
import styled from '@emotion/styled';
import clsx from 'clsx';

import { Typography } from '@common/styles';
import { colorWithAlpha } from '@common/utils';

import { useUblHandler } from '../../hooks/useUblHandler';
import { AdBannerBaseProps } from '../../types/AdBannerBaseProps';
import { linearGradient } from '../../utils/linearGradient';

interface Props extends AdBannerBaseProps, AnchorHTMLAttributes<HTMLAnchorElement> {}

export const BarAutoAdBanner: FC<React.PropsWithChildren<Props>> = ({ item, ubl_handler, onClick, href = item.landing_url, ...rest }) => {
  const { bind } = useUblHandler({ item, ubl_handler, onClick });

  return (
    <SC.Root {...rest} {...bind()} href={href}>
      <SC.ThumbnailContainer>
        <SC.Thumbnail src={item.banner_image_url} alt={item.title} />
      </SC.ThumbnailContainer>
      <SC.LinearGradient background_color={item.background_color}>
        <SC.CopyContainer>
          <p className={clsx('title', Typography.HEAD_22_BOLD)}>{item.title}</p>
          <p className={clsx('sub-title', Typography.BODY_14_MEDIUM)}>{item.sub_title}</p>
        </SC.CopyContainer>
      </SC.LinearGradient>
    </SC.Root>
  );
};

/**
 * [디자인 가이드]
 * https://www.figma.com/file/LixW5qVv3BRjxee6A1P72j/%EB%A9%80%ED%8B%B0%EB%B3%B4%EB%93%9C-%EA%B0%9C%EC%84%A0?type=design&node-id=1689-330349&mode=design&t=I7Ua7NIs6yezm7Al-4
 */
const CRITICAL_BANNER_WIDTH = 375; // 배너 기준 넓이
const CRITICAL_IMAGE_WIDTH = 184; // 이미지 기준 넓이
const CRITICAL_IMAGE_HEIGHT = 140; // 배너 기준 높이
const IMAGE_ASPECT_RATIO = CRITICAL_IMAGE_WIDTH / CRITICAL_IMAGE_HEIGHT; // 배너 aspect-ratio
const RESPONSIVE_BANNER_WIDTH = 'min(100vw, 720px)'; // 배너의 가변 사이즈
const IMAGE_WIDTH_PER_BANNER_WIDTH = CRITICAL_IMAGE_WIDTH / CRITICAL_BANNER_WIDTH; // 이미지 대 배너 넓이 비율
// 기준 넓이로 계산된 linear gradient 의 stop point(백분율)
const GRADIANT_STOP_POINTS = [(191 / CRITICAL_BANNER_WIDTH) * 100, (244 / CRITICAL_BANNER_WIDTH) * 100];
const MAIN_COPY_MAX_WIDTH = 171; // 메인카피 최대 넓이

const SC = {
  Root: styled.a`
    position: relative;
    display: flex;
    justify-content: flex-end;
    width: 100%;
    overflow: hidden;
    cursor: pointer;
  `,
  ThumbnailContainer: styled.div`
    width: ${CRITICAL_IMAGE_WIDTH}px;
    min-height: ${CRITICAL_IMAGE_HEIGHT}px;
    aspect-ratio: ${IMAGE_ASPECT_RATIO};

    @media (min-width: ${CRITICAL_BANNER_WIDTH}px) {
      width: ${IMAGE_WIDTH_PER_BANNER_WIDTH * 100}%;
    }

    @supports not (aspect-ratio: ${IMAGE_ASPECT_RATIO}) {
      position: relative;
      padding-top: calc(${CRITICAL_IMAGE_WIDTH}px / ${IMAGE_ASPECT_RATIO});

      @media (min-width: ${CRITICAL_BANNER_WIDTH}px) {
        padding-top: calc((${RESPONSIVE_BANNER_WIDTH}) * ${IMAGE_WIDTH_PER_BANNER_WIDTH / IMAGE_ASPECT_RATIO});
      }
    }
  `,
  Thumbnail: styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;

    @supports not (aspect-ratio: ${IMAGE_ASPECT_RATIO}) {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  `,
  LinearGradient: styled.div<{ background_color: string }>`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: inherit;
    background: ${({ background_color }) =>
      linearGradient([
        {
          color: background_color,
          stop: GRADIANT_STOP_POINTS[0],
        },
        {
          color: colorWithAlpha(background_color, 0),
          stop: GRADIANT_STOP_POINTS[1],
        },
      ])};
  `,
  CopyContainer: styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    padding: 0 20px;

    .title {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      color: ${Static_White};
      white-space: pre-wrap;
      text-overflow: ellipsis;
      overflow: hidden;
      max-width: ${MAIN_COPY_MAX_WIDTH}px;
      margin-bottom: 10px;
    }

    .sub-title {
      color: ${Static_White};
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
      opacity: 0.7;
    }
  `,
};
